import { useEffect, useState, useMemo, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { eventsApi } from "@/api/events";
import { speakersApi } from "@/api/speakers";
import {
  t, Icon, ICONS, Btn,
} from "@/components/ui";
import type { Event, Stage, Session, Speaker, Sponsor } from "@/types";

/**
 * Slide-in exports panel. Mounted at the AppShell layer, opened from
 * either the Agenda toolbar or the sidebar nav item. All exports for
 * the active event live here, grouped into sections.
 */

type FormatTag = "docx" | "xlsx" | "pdf" | "csv" | "ics";

interface FilterOption { value: string; label: string }

interface FilterDef {
  key: string;
  label: string;
  options: FilterOption[];
  default?: string;
}

interface ExportCardDef {
  id: string;
  icon: keyof typeof ICONS;
  iconColor?: string;
  title: string;
  body: string;
  formats: FormatTag[];
  aiBadge?: boolean;
  filters?: FilterDef[];
  disabled?: boolean;
  disabledReason?: string;
  /** Called with the current filter values. Should perform a download. */
  onDownload: (values: Record<string, string>) => Promise<void>;
}

interface SectionDef {
  id: string;
  label: string;
  icon: keyof typeof ICONS;
  cards: ExportCardDef[];
}

export default function ExportsPanel({
  open, onClose, event,
}: {
  open: boolean;
  onClose: () => void;
  event: Event | undefined;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ESC + scroll lock
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Load filters' source data only when the panel is open
  const { data: stages = [] } = useQuery<Stage[]>({
    queryKey: ["stages", event?.id],
    queryFn: () => eventsApi.listStages(event!.id),
    enabled: open && !!event?.id,
    staleTime: 30_000,
  });

  const { data: sessions = [] } = useQuery<Session[]>({
    queryKey: ["sessions", event?.id],
    queryFn: () => eventsApi.listSessions(event!.id),
    enabled: open && !!event?.id,
    staleTime: 30_000,
  });

  const { data: speakers = [] } = useQuery<Speaker[]>({
    queryKey: ["speakers"],
    queryFn: () => speakersApi.list(),
    enabled: open,
    staleTime: 30_000,
  });

  const { data: eventDetail } = useQuery<Event & { sponsors: Sponsor[] }>({
    queryKey: ["event-detail", event?.id],
    queryFn: () => eventsApi.get(event!.id) as Promise<Event & { sponsors: Sponsor[] }>,
    enabled: open && !!event?.id,
    staleTime: 30_000,
  });
  const sponsors: Sponsor[] = (eventDetail as { sponsors?: Sponsor[] })?.sponsors ?? [];

  // Build day options from event range
  const eventDays = useMemo(() => {
    if (!event) return [];
    const out: string[] = [];
    const s = new Date(event.startDate);
    const e = new Date(event.endDate);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return out;
    const cur = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate()));
    const last = new Date(Date.UTC(e.getUTCFullYear(), e.getUTCMonth(), e.getUTCDate()));
    while (cur.getTime() <= last.getTime()) {
      out.push(cur.toISOString().slice(0, 10));
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return out;
  }, [event?.startDate, event?.endDate]);

  const dayOptions: FilterOption[] = [
    { value: "all", label: "Toutes les journées" },
    ...eventDays.map(d => ({
      value: d,
      label: new Date(d + "T00:00:00Z").toLocaleDateString("fr-FR", {
        weekday: "short", day: "numeric", month: "short", timeZone: "UTC",
      }),
    })),
  ];

  const stageOptions: FilterOption[] = [
    { value: "all", label: "Toutes les scènes" },
    ...stages.map(s => ({ value: s.id, label: s.name })),
  ];

  // Format rules → ceremony sessions for MC script default
  const ceremonySessions = useMemo(() => {
    if (!event?.formatRules) return [];
    const rules = event.formatRules ?? {};
    const ceremonyFormats = new Set(
      Object.entries(rules)
        .filter(([, r]) => r?.isCeremony)
        .map(([k]) => k.toLowerCase()),
    );
    return sessions.filter(s => ceremonyFormats.has(s.format.toLowerCase()));
  }, [event?.formatRules, sessions]);

  const sessionOptions: FilterOption[] = [
    { value: "all", label: "Toutes les sessions" },
    ...sessions
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
      .map(s => ({
        value: s.id,
        label: `${s.startTime} · ${s.title.length > 40 ? s.title.slice(0, 38) + "…" : s.title}`,
      })),
  ];

  const speakerOptions: FilterOption[] = [
    { value: "all", label: "Tous les speakers confirmés" },
    ...speakers
      .slice()
      .sort((a, b) => a.lastName.localeCompare(b.lastName))
      .map(sp => ({ value: sp.id, label: `${sp.firstName} ${sp.lastName}` })),
  ];

  const sponsorOptions: FilterOption[] = [
    { value: "all", label: "Tous les sponsors" },
    ...sponsors.map(sp => ({ value: sp.id, label: `${sp.name} · ${sp.tier}` })),
  ];

  if (!event) return null;

  // ── Card definitions ──────────────────────────────────────────────────────

  const sections: SectionDef[] = [
    {
      id: "production",
      label: "Production",
      icon: "activity",
      cards: [
        {
          id: "conducteur",
          icon: "file",
          iconColor: t.accent,
          title: "Conducteur technique",
          body: "Tableau régie complet (Seq, heures, durée, action, intervenants, images, son, vidéo, notes).",
          formats: ["docx"],
          filters: [
            { key: "day", label: "Jour", options: dayOptions, default: "all" },
            { key: "stageId", label: "Scène", options: stageOptions, default: "all" },
          ],
          onDownload: vals =>
            eventsApi.downloadConducteur(event.id, {
              day: vals.day !== "all" ? vals.day : undefined,
              stageId: vals.stageId !== "all" ? vals.stageId : undefined,
            }),
        },
        {
          id: "mc-script",
          icon: "mail",
          iconColor: "#7c3aed",
          title: "Conducteur MC",
          body: "Texte parlé pour le maître de cérémonie : annonces de session, intros speaker, transitions, mentions sponsor. Génération IA en ~10 s.",
          formats: ["docx"],
          aiBadge: true,
          filters: [
            { key: "day", label: "Jour", options: dayOptions, default: "all" },
            { key: "stageId", label: "Scène", options: stageOptions, default: "all" },
          ],
          onDownload: vals =>
            eventsApi.downloadMCScript(event.id, {
              day: vals.day !== "all" ? vals.day : undefined,
              stageId: vals.stageId !== "all" ? vals.stageId : undefined,
            }),
        },
        {
          id: "run-of-show",
          icon: "list",
          iconColor: t.info,
          title: "Run of Show",
          body: "Vue d'ensemble jour par jour pour la coordination terrain.",
          formats: ["pdf"],
          filters: [
            { key: "day", label: "Jour", options: dayOptions, default: "all" },
          ],
          disabled: true,
          disabledReason: "Format PDF en attente",
          onDownload: async () => { toast.info("Format PDF en attente."); },
        },
        {
          id: "briefing",
          icon: "edit",
          iconColor: "#0891b2",
          title: "Briefing modérateur",
          body: "Brief par session : description, intervenants, bios, questions suggérées.",
          formats: ["docx"],
          aiBadge: true,
          filters: [
            {
              key: "sessionId",
              label: "Session",
              options: [{ value: "all", label: "Toutes les sessions" }, ...sessionOptions.filter(o => o.value !== "all")],
              default: ceremonySessions[0]?.id ?? sessions[0]?.id ?? "all",
            },
          ],
          onDownload: async (vals) => {
            if (vals.sessionId === "all") {
              toast.error("Choisissez une session précise pour le briefing.");
              return;
            }
            await eventsApi.downloadBriefing(event.id, vals.sessionId);
          },
        },
      ],
    },
    {
      id: "speakers",
      label: "Speakers",
      icon: "users",
      cards: [
        {
          id: "speaker-directory",
          icon: "users",
          iconColor: t.success,
          title: "Annuaire speakers",
          body: "Tous les intervenants avec photo, bio et sessions assignées.",
          formats: ["docx"],
          onDownload: () => eventsApi.downloadSpeakers(event.id),
        },
        {
          id: "speaker-sheet",
          icon: "file",
          iconColor: "#db2777",
          title: "Fiche de confirmation",
          body: "Confirmation individuelle envoyée à un speaker : programme, scène, rôle, co-panelistes, check-list à préparer.",
          formats: ["docx"],
          filters: [
            { key: "speakerId", label: "Speaker", options: speakerOptions.filter(o => o.value !== "all"), default: speakers[0]?.id ?? "" },
          ],
          onDownload: async (vals) => {
            if (!vals.speakerId) {
              toast.error("Choisissez un speaker.");
              return;
            }
            await eventsApi.downloadSpeakerSheet(event.id, vals.speakerId);
          },
        },
        {
          id: "speaker-bios",
          icon: "edit",
          iconColor: "#ea580c",
          title: "Bios presse",
          body: "Bios formatées pour les communiqués et la presse, prêtes à copier. Filtrable à une session.",
          formats: ["docx"],
          filters: [
            { key: "sessionId", label: "Session", options: sessionOptions, default: "all" },
          ],
          onDownload: vals =>
            eventsApi.downloadSpeakerBios(event.id, {
              sessionId: vals.sessionId !== "all" ? vals.sessionId : undefined,
            }),
        },
      ],
    },
    {
      id: "data",
      label: "Données",
      icon: "grid",
      cards: [
        {
          id: "agenda-csv",
          icon: "download",
          iconColor: t.muted,
          title: "Agenda CSV",
          body: "Toutes les sessions sous forme de tableau : date, heure, titre, format, scène, statut, speakers.",
          formats: ["csv"],
          filters: [
            { key: "day", label: "Jour", options: dayOptions, default: "all" },
            { key: "stageId", label: "Scène", options: stageOptions, default: "all" },
          ],
          onDownload: vals =>
            eventsApi.downloadAgendaCSV(event.id, {
              day: vals.day !== "all" ? vals.day : undefined,
              stageId: vals.stageId !== "all" ? vals.stageId : undefined,
            }),
        },
        {
          id: "ics",
          icon: "calendar",
          iconColor: t.accent,
          title: "Calendrier ICS",
          body: "Fichier .ics pour Google Calendar, Outlook ou Apple Calendar.",
          formats: ["ics"],
          onDownload: () => eventsApi.downloadICS(event.id),
        },
      ],
    },
    {
      id: "sponsors",
      label: "Sponsors",
      icon: "globe",
      cards: [
        {
          id: "sponsor-recap",
          icon: "globe",
          iconColor: t.warning,
          title: "Récap sponsor",
          body: "Synthèse par sponsor : sessions parrainées, visibilité accordée, tier.",
          formats: ["docx"],
          filters: [
            { key: "sponsorId", label: "Sponsor", options: sponsorOptions.filter(o => o.value !== "all"), default: sponsors[0]?.id ?? "" },
          ],
          disabled: sponsors.length === 0,
          disabledReason: sponsors.length === 0 ? "Aucun sponsor sur cet événement" : undefined,
          onDownload: async (vals) => {
            if (!vals.sponsorId) {
              toast.error("Choisissez un sponsor.");
              return;
            }
            await eventsApi.downloadSponsorRecap(event.id, vals.sponsorId);
          },
        },
      ],
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 90,
          background: open ? "rgba(15, 23, 42, 0.42)" : "transparent",
          backdropFilter: open ? "blur(4px)" : "none",
          WebkitBackdropFilter: open ? "blur(4px)" : "none",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 180ms ease, backdrop-filter 180ms ease",
        }}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Exports"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          zIndex: 91,
          width: "min(540px, 96vw)",
          background: t.bg,
          borderLeft: `1px solid ${t.border}`,
          boxShadow: open ? "-24px 0 60px rgba(15, 23, 42, 0.18)" : "none",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1)",
          display: "flex", flexDirection: "column",
          fontFamily: t.fontSans,
        }}
      >
        {/* Header */}
        <div style={{
          padding: "18px 22px",
          borderBottom: `1px solid ${t.border}`,
          background: t.surface,
          display: "flex", alignItems: "flex-start", gap: 16,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 11, fontFamily: t.fontMono,
              fontWeight: 600, color: t.accent,
              textTransform: "uppercase", letterSpacing: "0.08em",
              marginBottom: 6,
            }}>
              Exports
            </div>
            <h2 style={{
              margin: 0, fontSize: 19, fontWeight: 700,
              color: t.text, letterSpacing: "-0.02em",
            }}>
              Tous les documents pour cet événement
            </h2>
            <p style={{
              margin: "6px 0 0", fontSize: 12, color: t.muted, lineHeight: 1.5,
            }}>
              {event.name} · choisissez un document, ajustez les filtres, téléchargez.
            </p>
          </div>
          <button
            onClick={onClose}
            title="Fermer"
            style={{
              background: "transparent", border: "none",
              color: t.muted, cursor: "pointer",
              padding: 6, borderRadius: t.radiusSm,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: `background ${t.transitionFast}, color ${t.transitionFast}`,
              flexShrink: 0,
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = t.surfaceHover;
              (e.currentTarget as HTMLElement).style.color = t.text;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = "transparent";
              (e.currentTarget as HTMLElement).style.color = t.muted;
            }}
          >
            <Icon d={ICONS.x} size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 22px 32px" }}>
          {sections.map(section => (
            <Section key={section.id} section={section}>
              {section.cards.map(card => (
                <ExportCard
                  key={card.id}
                  card={card}
                  expanded={expandedId === card.id}
                  onToggle={() =>
                    setExpandedId(prev => (prev === card.id ? null : card.id))
                  }
                />
              ))}
            </Section>
          ))}
        </div>
      </aside>
    </>
  );
}

function Section({ section, children }: { section: SectionDef; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        fontSize: 11, fontWeight: 600,
        color: t.muted, fontFamily: t.fontMono,
        textTransform: "uppercase", letterSpacing: "0.08em",
        marginBottom: 12,
      }}>
        <Icon d={ICONS[section.icon]} size={12} />
        {section.label}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{children}</div>
    </div>
  );
}

function ExportCard({
  card, expanded, onToggle,
}: {
  card: ExportCardDef;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [hover, setHover] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of card.filters ?? []) init[f.key] = f.default ?? f.options[0]?.value ?? "";
    return init;
  });

  async function handleDownload() {
    if (card.disabled) return;
    setDownloading(true);
    try {
      await card.onDownload(filterValues);
    } catch (err: unknown) {
      toast.error((err as Error)?.message ?? "Échec du téléchargement.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: t.surface,
        border: `1px solid ${expanded ? t.accent + "60" : (hover ? t.borderStrong : t.border)}`,
        borderRadius: t.radius,
        boxShadow: hover && !expanded ? t.shadowHover : t.shadow,
        transform: hover && !expanded ? "translateY(-1px)" : "translateY(0)",
        transition: `box-shadow ${t.transition}, transform ${t.transition}, border-color ${t.transition}`,
        opacity: card.disabled ? 0.65 : 1,
        overflow: "hidden",
      }}
    >
      <button
        onClick={onToggle}
        disabled={card.disabled}
        style={{
          width: "100%", background: "transparent", border: "none",
          cursor: card.disabled ? "not-allowed" : "pointer",
          padding: "14px 16px",
          display: "flex", alignItems: "flex-start", gap: 14,
          textAlign: "left", fontFamily: t.fontSans, color: t.text,
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: (card.iconColor ?? t.accent) + "16",
          color: card.iconColor ?? t.accent,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Icon d={ICONS[card.icon]} size={17} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: t.text, letterSpacing: "-0.01em" }}>
              {card.title}
            </span>
            {card.aiBadge && (
              <span style={{
                fontSize: 9, fontWeight: 700, fontFamily: t.fontMono,
                color: "#7c3aed", background: "#7c3aed18",
                padding: "2px 6px", borderRadius: t.radiusPill,
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>
                AI
              </span>
            )}
            {card.formats.map(fmt => (
              <span key={fmt} style={{
                fontSize: 9, fontWeight: 700, fontFamily: t.fontMono,
                color: t.muted, background: t.surfaceSunken,
                padding: "2px 6px", borderRadius: t.radiusPill,
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>
                {fmt}
              </span>
            ))}
            {card.disabled && (
              <span style={{
                fontSize: 9, fontWeight: 700, fontFamily: t.fontMono,
                color: t.warning, background: t.warning + "18",
                padding: "2px 6px", borderRadius: t.radiusPill,
                textTransform: "uppercase", letterSpacing: "0.06em",
                marginLeft: "auto",
              }}>
                À venir
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: t.muted, lineHeight: 1.5 }}>
            {card.body}
          </div>
        </div>
        <div style={{
          color: t.muted, marginLeft: 4, display: "flex", alignItems: "center",
          transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
          transition: `transform ${t.transition}`,
          flexShrink: 0, marginTop: 8,
        }}>
          <Icon d={ICONS.chevronRight} size={15} />
        </div>
      </button>

      {expanded && !card.disabled && (
        <div style={{
          padding: "0 16px 16px 66px",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          {card.filters && card.filters.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
              {card.filters.map(f => (
                <FilterSelect
                  key={f.key}
                  label={f.label}
                  value={filterValues[f.key] ?? ""}
                  options={f.options}
                  onChange={v => setFilterValues(prev => ({ ...prev, [f.key]: v }))}
                />
              ))}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn
              small
              variant="primary"
              icon="download"
              disabled={downloading}
              onClick={handleDownload}
            >
              {downloading ? "Téléchargement…" : "Télécharger"}
            </Btn>
          </div>
        </div>
      )}

      {expanded && card.disabled && card.disabledReason && (
        <div style={{
          padding: "0 16px 16px 66px",
          fontSize: 12, color: t.muted, fontStyle: "italic",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <Icon d={ICONS.clock} size={12} />
          {card.disabledReason}
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  label, value, options, onChange,
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{
        fontSize: 10, fontFamily: t.fontMono,
        fontWeight: 600, color: t.muted,
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>
        {label}
      </span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          padding: "7px 10px",
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: t.radiusSm,
          color: t.text, fontSize: 13,
          fontFamily: t.fontSans, outline: "none",
          cursor: "pointer",
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

export type { ExportCardDef };
